/**
 * Organization Service
 *
 * This service handles all operations related to organizations, including:
 * - Creating and managing organizations
 * - Managing organization members
 * - Handling invitations
 *
 * Note: The acceptInvitation function has been enhanced to handle cases where
 * the user document doesn't exist in Firestore. It will now create the user
 * document on the fly if needed, rather than throwing an error.
 */

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Organization, OrganizationMember, Role, OrganizationInvitation } from '../types';
import { userService } from './userService';
import { notificationService } from './notificationService';
import { vehicleService } from './vehicleService';

export const organizationService = {
  async createOrganization(name: string, userId: string): Promise<string> {
    try {
      const batch = writeBatch(db);
      
      // Create organization document
      const organizationRef = doc(collection(db, 'organizations'));
      batch.set(organizationRef, {
        name,
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Get user data for member creation
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      const userData = userDoc.data();

      // Add creator as admin member with email
      const memberRef = doc(db, `organizations/${organizationRef.id}/members/${userId}`);
      batch.set(memberRef, {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email || userDoc.data().email, // Ensure we get the email
        functionTitle: 'Owner',
        role: 'Admin',
        joinedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update user's organization ID
      const userRef = doc(db, 'users', userId);
      batch.update(userRef, {
        organizationId: organizationRef.id,
        updatedAt: serverTimestamp()
      });

      await batch.commit();
      return organizationRef.id;
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  },

  async getOrganization(organizationId: string): Promise<Organization | null> {
    try {
      const organizationRef = doc(db, `organizations/${organizationId}`);
      const organizationDoc = await getDoc(organizationRef);
      
      if (!organizationDoc.exists()) {
        return null;
      }
      
      return {
        id: organizationDoc.id,
        ...organizationDoc.data()
      } as Organization;
    } catch (error) {
      console.error('Error getting organization:', error);
      throw error;
    }
  },

  async isUserAdmin(organizationId: string, userId: string): Promise<boolean> {
    try {
      const memberRef = doc(db, `organizations/${organizationId}/members/${userId}`);
      const memberDoc = await getDoc(memberRef);
      
      if (!memberDoc.exists()) {
        return false;
      }
      
      return memberDoc.data().role === 'Admin';
    } catch (error) {
      console.error('Error checking admin status:', error);
      throw error;
    }
  },

  async findOrganizationsByInvitationEmail(email: string): Promise<{ organization: Organization; invitation: OrganizationInvitation }[]> {
    try {
      console.log('Finding organizations with invitations for email:', email);
      const organizationsRef = collection(db, 'organizations');
      const results: { organization: Organization; invitation: OrganizationInvitation }[] = [];
      
      // Get all organizations
      const organizationsSnapshot = await getDocs(organizationsRef);
      console.log(`Found ${organizationsSnapshot.docs.length} organizations to check`);
      
      // For each organization, check its invitations
      const orgPromises = organizationsSnapshot.docs.map(async (orgDoc) => {
        try {
          const orgId = orgDoc.id;
          const orgData = orgDoc.data();
          console.log(`Checking organization: ${orgId}`);
          
          const invitationsRef = collection(db, `organizations/${orgId}/invitations`);
          const invitationsQuery = query(invitationsRef,
            where('email', '==', email),
            where('status', '==', 'pending')
          );
          
          const invitationsSnapshot = await getDocs(invitationsQuery);
          console.log(`Found ${invitationsSnapshot.docs.length} invitations in org ${orgId}`);
          
          if (!invitationsSnapshot.empty) {
            // Process each invitation found
            for (const invitationDoc of invitationsSnapshot.docs) {
              try {
                // Get the invitation details separately to ensure we have permission
                const invitationDetails = await getDoc(doc(invitationsRef, invitationDoc.id));
                
                if (invitationDetails.exists()) {
                  console.log(`Found valid invitation: ${invitationDetails.id} in org ${orgId}`);
                  results.push({
                    organization: {
                      id: orgId,
                      ...orgData
                    } as Organization,
                    invitation: {
                      id: invitationDetails.id,
                      ...invitationDetails.data()
                    } as OrganizationInvitation
                  });
                }
              } catch (invDetailError) {
                console.warn(`Error getting invitation details for ${invitationDoc.id}:`, invDetailError);
                // Continue to next invitation
              }
            }
          }
        } catch (orgError) {
          console.warn('Error checking invitations for organization:', orgDoc.id, orgError);
          // Continue checking other organizations
        }
      });
      
      // Wait for all organization checks to complete
      await Promise.allSettled(orgPromises);
      
      console.log(`Found ${results.length} total valid invitations`);
      return results;
    } catch (error) {
      console.error('Error finding organizations by invitation email:', error);
      throw error;
    }
  },

  async cancelInvitation(organizationId: string, invitationId: string): Promise<void> {
    try {
      console.log('Canceling invitation...', { organizationId, invitationId });
      
      const invitationRef = doc(db, `organizations/${organizationId}/invitations/${invitationId}`);
      const invitationDoc = await getDoc(invitationRef);
      
      if (!invitationDoc.exists()) {
        throw new Error('Invitation not found');
      }

      await deleteDoc(invitationRef);
      console.log('Invitation canceled successfully');
    } catch (error) {
      console.error('Error in cancelInvitation:', error);
      throw error;
    }
  },

  async acceptInvitation(organizationId: string, invitationId: string, userId: string): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      // Get the invitation details
      const invitationRef = doc(db, `organizations/${organizationId}/invitations/${invitationId}`);
      const invitationDoc = await getDoc(invitationRef);
      
      if (!invitationDoc.exists()) {
        throw new Error('Invitation not found');
      }
      
      const invitation = invitationDoc.data() as OrganizationInvitation;
      console.log('Accepting invitation with data:', invitation);
      
      // Get user data
      const userRef = doc(db, `users/${userId}`);
      const userDoc = await getDoc(userRef);
      
      let userData: any = {};
      
      // If user document doesn't exist, create it
      if (!userDoc.exists()) {
        console.log('User document not found, creating it...');
        
        // Extract name from email (simple approach)
        const emailName = invitation.email.split('@')[0] || 'User';
        
        // Create basic user data
        userData = {
          email: invitation.email,
          firstName: emailName,
          lastName: '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        // Set the user document
        batch.set(userRef, userData);
        
        // Create default notification preferences
        const notifRef = doc(db, `users/${userId}/settings/notifications`);
        batch.set(notifRef, {
          enabled: false,
          maintenance: true,
          fuel: true,
          mileage: true,
          updatedAt: new Date().toISOString()
        });
      } else {
        userData = userDoc.data();
      }
      
      // Update invitation status (only status and updatedAt as per security rules)
      const invitationUpdate = {
        status: 'accepted' as const,
        updatedAt: serverTimestamp()
      };
      batch.update(invitationRef, invitationUpdate);
      
      // Add user as organization member with verified email
      const memberRef = doc(db, `organizations/${organizationId}/members/${userId}`);
      batch.set(memberRef, {
        firstName: userData.firstName || invitation.email.split('@')[0] || 'User',
        lastName: userData.lastName || '',
        email: userData.email || invitation.email,
        functionTitle: invitation.functionTitle,
        role: invitation.role,
        joinedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Update user's profile with organization ID
      batch.update(userRef, {
        organizationId,
        updatedAt: serverTimestamp()
      });

      // Commit the batch to create the member and update user
      await batch.commit();
      console.log('Member created successfully');

      // If a vehicle was assigned in the invitation, handle the assignment separately
      if (invitation.vehicleId) {
        console.log('Assigning vehicle:', invitation.vehicleId, 'to user:', userId);
        try {
          await vehicleService.assignVehicle(invitation.vehicleId, userId);
          console.log('Vehicle assigned successfully');
        } catch (error) {
          console.error('Error assigning vehicle:', error);
          // Don't throw here - the user is already a member, just log the error
        }
      } else {
        console.log('No vehicle to assign');
      }
    } catch (error) {
      console.error('Error in acceptInvitation:', error);
      throw error;
    }
  },

  async resendInvitation(organizationId: string, invitationId: string): Promise<void> {
    try {
      // Get invitation details
      const invitationRef = doc(db, `organizations/${organizationId}/invitations/${invitationId}`);
      const invitationDoc = await getDoc(invitationRef);
      
      if (!invitationDoc.exists()) {
        throw new Error('Invitation not found');
      }

      const invitation = invitationDoc.data() as OrganizationInvitation;

      // Get organization details for the email
      const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
      if (!orgDoc.exists()) {
        throw new Error('Organization not found');
      }
      const orgName = orgDoc.data().name;

      // Update invitation timestamp
      await updateDoc(invitationRef, {
        updatedAt: serverTimestamp()
      });

      // Resend invitation email
      await notificationService.sendNotificationEmail({
        to_email: invitation.email,
        to_name: invitation.email.split('@')[0],
        subject: `Invitation à rejoindre ${orgName} (renvoyée)`,
        message: notificationService.formatInvitationMessage(orgName, invitation.role, invitation.functionTitle || '', invitation.email)
      });

      console.log('Invitation resent successfully');
    } catch (error) {
      console.error('Error resending invitation:', error);
      throw error;
    }
  },

  async declineInvitation(organizationId: string, invitationId: string): Promise<void> {
    try {
      const invitationRef = doc(db, `organizations/${organizationId}/invitations/${invitationId}`);
      const invitationDoc = await getDoc(invitationRef);
      
      if (!invitationDoc.exists()) {
        throw new Error('Invitation not found');
      }
      
      // Update invitation status (only status and updatedAt as per security rules)
      const invitationUpdate = {
        status: 'rejected' as const,
        updatedAt: serverTimestamp()
      };
      await updateDoc(invitationRef, invitationUpdate);
    } catch (error) {
      console.error('Error in declineInvitation:', error);
      throw error;
    }
  },

  async getOrganizationMembers(organizationId: string): Promise<OrganizationMember[]> {
    try {
      const membersRef = collection(db, `organizations/${organizationId}/members`);
      const membersSnapshot = await getDocs(membersRef);
      
      return membersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          userId: doc.id,
          email: data.email,
          displayName: `${data.firstName} ${data.lastName}`.trim(),
          role: data.role,
          functionTitle: data.functionTitle,
          joinedAt: data.joinedAt,
          status: data.status || 'active'
        } as OrganizationMember;
      });
    } catch (error) {
      console.error('Error getting organization members:', error);
      throw error;
    }
  },

  async getPendingInvitations(organizationId: string): Promise<OrganizationInvitation[]> {
    try {
      const invitationsRef = collection(db, `organizations/${organizationId}/invitations`);
      const q = query(invitationsRef, where('status', '==', 'pending'));
      const invitationsSnapshot = await getDocs(q);
      
      return invitationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as OrganizationInvitation));
    } catch (error) {
      console.error('Error getting pending invitations:', error);
      throw error;
    }
  },

  async inviteMember(
    organizationId: string,
    email: string,
    role: Role,
    functionTitle: string,
    vehicleId?: string
  ): Promise<void> {
    try {
      // Check if user already has a pending invitation
      const existingInvitations = await this.findOrganizationsByInvitationEmail(email);
      if (existingInvitations.some(inv => inv.organization.id === organizationId)) {
        throw new Error('User already has a pending invitation to this organization');
      }

      // Get organization details for the email
      const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
      if (!orgDoc.exists()) {
        throw new Error('Organization not found');
      }
      const orgName = orgDoc.data().name;

      // Create invitation document
      const invitationRef = doc(collection(db, `organizations/${organizationId}/invitations`));
      const invitationData: any = {
        email,
        role,
        functionTitle,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        organizationId
      };

      // Only include vehicleId if it's defined
      if (vehicleId !== undefined) {
        invitationData.vehicleId = vehicleId;
      }

      await setDoc(invitationRef, invitationData);

      // Envoyer l'email d'invitation
      await notificationService.sendNotificationEmail({
        to_email: email,
        to_name: email.split('@')[0], // Utiliser la partie avant @ comme nom
        subject: `Invitation à rejoindre ${orgName}`,
        message: notificationService.formatInvitationMessage(orgName, role, functionTitle, email)
      });

      console.log('Invitation created and email sent successfully');
    } catch (error) {
      console.error('Error inviting member:', error);
      throw error;
    }
  },

  async updateMemberRole(organizationId: string, memberId: string, newRole: Role): Promise<void> {
    try {
      const memberRef = doc(db, `organizations/${organizationId}/members/${memberId}`);
      await updateDoc(memberRef, {
        role: newRole,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating member role:', error);
      throw error;
    }
  },

  async removeMember(organizationId: string, memberId: string): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      // Remove member from organization
      const memberRef = doc(db, `organizations/${organizationId}/members/${memberId}`);
      batch.delete(memberRef);
      
      // Update user's profile to remove organization
      const userRef = doc(db, 'users', memberId);
      batch.update(userRef, {
        organizationId: null,
        updatedAt: serverTimestamp()
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error removing member:', error);
      throw error;
    }
  },

  async getOrganizationAdmins(organizationId: string): Promise<{ email: string; displayName?: string }[]> {
    try {
      const membersRef = collection(db, `organizations/${organizationId}/members`);
      const q = query(membersRef, where('role', '==', 'Admin'));
      const membersSnapshot = await getDocs(q);
      
      return membersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          email: data.email,
          displayName: `${data.firstName} ${data.lastName}`.trim()
        };
      });
    } catch (error) {
      console.error('Error getting organization admins:', error);
      throw error;
    }
  },

  async deleteOrganization(organizationId: string): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      // Get all members
      const membersRef = collection(db, `organizations/${organizationId}/members`);
      const membersSnapshot = await getDocs(membersRef);
      
      // Update all members' user profiles to remove organization
      for (const memberDoc of membersSnapshot.docs) {
        const userRef = doc(db, `users/${memberDoc.id}`);
        batch.update(userRef, {
          organizationId: null,
          updatedAt: serverTimestamp()
        });
        // Delete member document
        batch.delete(memberDoc.ref);
      }
      
      // Delete all pending invitations
      const invitationsRef = collection(db, `organizations/${organizationId}/invitations`);
      const invitationsSnapshot = await getDocs(invitationsRef);
      invitationsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Delete the organization document itself
      const organizationRef = doc(db, `organizations/${organizationId}`);
      batch.delete(organizationRef);
      
      await batch.commit();
    } catch (error) {
      console.error('Error deleting organization:', error);
      throw error;
    }
  }
};
